import { useCallback, useEffect, useId, useRef, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn';
import { Button } from './Button';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_PX: Record<ModalSize, number> = {
  sm: 400,
  md: 560,
  lg: 720,
  xl: 960,
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  size?: ModalSize;
  dismissible?: boolean;
  footer?: ReactNode;
  children: ReactNode;
  /** Optional override — e.g. wider than the size presets. Falls back to size. */
  maxWidthPx?: number;
  className?: string;
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  dismissible = true,
  footer,
  children,
  maxWidthPx,
  className,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const handleClose = useCallback(() => {
    if (dismissible) onClose();
  }, [dismissible, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const panel = panelRef.current;
        if (!panel) return;
        const focusable = panel.querySelectorAll<HTMLElement>(FOCUSABLE);
        if (focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, dismissible, onClose]);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const first = panel.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const panelStyle: CSSProperties = {
    maxWidth: `${maxWidthPx ?? SIZE_PX[size]}px`,
  };

  return createPortal(
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
    >
      {/* V2.1-I — liquid-glass scrim. backdrop-blur (12px) + slight saturate
          adds depth without obscuring the active modal. Browsers without
          backdrop-filter (older Firefox, WebView fallbacks) get the
          ink-0/30 tint only — graceful. */}
      <div
        className="absolute inset-0 bg-ink-0/30 backdrop-blur-md backdrop-saturate-[1.2] animate-[modalScrim_200ms_cubic-bezier(0.2,0.7,0.2,1)]"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        style={panelStyle}
        className={cn(
          'relative bg-paper-0 rounded-token-xl shadow-3 w-full max-h-[90vh] overflow-auto',
          'animate-[modalPanel_200ms_cubic-bezier(0.2,0.7,0.2,1)]',
          className,
        )}
      >
        {title || dismissible ? (
          <header className="flex items-start justify-between gap-4 px-5 pt-5">
            <div className="flex flex-col gap-0.5">
              {title ? (
                <h2 id={titleId} className="text-h2 text-ink-0 font-semibold">
                  {title}
                </h2>
              ) : null}
              {subtitle ? <p className="text-meta text-ink-2">{subtitle}</p> : null}
            </div>
            {dismissible ? (
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="Luk dialog"
                onClick={onClose}
                leadingIcon={<CloseIcon />}
              />
            ) : null}
          </header>
        ) : null}
        <section className="p-5">{children}</section>
        {footer ? (
          <footer className="flex items-center justify-end gap-2 border-t border-line-1 px-5 py-4">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

function CloseIcon() {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 4L12 12M12 4L4 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
